import { Immutable } from "immer";
import React from "react";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";

import { templates } from "@reactivated";
import type {
    FourByOneImageBlock as FourByOneImageBlockValue,
    HomeMultiProdModule as HomeMultiProdModuleValue,
    UGCGrid as UGCGridValue,
    ValuePropBarBlock as ValuePropBarBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { FinancingModalTriggerHomepage } from "../../apps/checkout/FinancingModalTriggerHomepage";
import {
    ToolboxPageSSR,
    buildToolboxPageMap,
} from "../../blocks/toolboxPageMap";
import { FourByOneImageBlock } from "../FourByOneImageBlock";
import { HomeMultiProdModule } from "../HomeMultiProdModule";
import { UGCGridBlock } from "../UGCGridBlock";

/**
 * tempurpedic HomePage ``modules`` body assembly (#33588).
 *
 * ``HomeModuleBlock`` = the shared ``Toolbox`` plus FIVE extra keys that are NOT
 * in ``ToolboxContent`` (they appear only on the HomePage):
 *   - ``value_props`` -> the shared ``ValuePropBarBlock`` (prop ``valuePropsData``,
 *     mirroring stearns' home ``value_props``);
 *   - ``pre_qualification_bar`` -> the interactive ``FinancingModalTriggerHomepage``
 *     island (``application_source="Toolbox"``), gated on
 *     ``PreQualificationSettings.toolbox`` (threaded via the
 *     ``prequal_enabled_ssr`` side-channel, keyed by block.id). Rendered as a
 *     REAL React child island so it hydrates -- NOT baked into an HTML string
 *     (the nested-island trap, af30dca2bc);
 *   - ``reviews_module`` -> ``FourByOneImageBlock`` (``reviews_url`` from the
 *     ``reviews_url_ssr`` side-channel -- legacy ``{% slugurl 'reviews' %}``);
 *   - ``multi_prod_module`` -> ``HomeMultiProdModule``;
 *   - ``ugc_grid`` -> ``UGCGrid``.
 * These five are layered ON TOP of ``buildToolboxPageMap`` (which already
 * supplies every Toolbox key + the page side-channel overrides). They live only
 * on the top-level HomeModuleBlock StreamBlock (never nested in a tab panel /
 * promo content), so spreading them on top of the page map is safe -- the
 * recursive container entries never need them.
 *
 * WRAPPER PARITY (load-bearing): the legacy ``home_page.html`` rendered
 * ``{% include_block self.modules %}`` -- the WHOLE StreamField -- so Wagtail's
 * ``StreamBlock.render_basic`` wrapped every top-level block in
 * ``<div class="w-block-{type} block-{type}">``. Reproduced per top-level block
 * via the per-block ``StreamField tagName="div"`` render (BasicPage precedent;
 * contrast sealy's HomePage, whose legacy was a bare ``{% for %}`` loop ->
 * Fragment). The HomePage's ``content`` field is a SEPARATE include_react
 * (``HomeContentAssembly``) and is unchanged.
 */
type IProps = Immutable<
    Pick<
        templates.HomePageTemplate,
        | "page"
        | "hardcoded_ssr"
        | "hotspot_ssr"
        | "breadcrumbs_ssr"
        | "customer_service_ssr"
        | "shop_compare_ssr"
        | "tab_feature_attribute_ssr"
        | "reviews_ssr"
        | "reviews_url_ssr"
        | "prequal_enabled_ssr"
    >
>;

/**
 * The page-derived SSR side-channels the HomePage `modules` component map closes
 * over. Split out from `IProps` so the styleguide block-render registry can build
 * the same map with minimal (zeroed) props (the fixture path threads no page).
 */
type IMapProps = Immutable<
    ToolboxPageSSR &
        Pick<
            templates.HomePageTemplate,
            "prequal_enabled_ssr" | "reviews_url_ssr"
        >
>;

export const buildHomePageBodyComponentsMap = (props: IMapProps) => {
    const map = {
        ...buildToolboxPageMap(props),

        // `value_props` -> ValuePropsBarBlock (prop `valuePropsData`). Distinct
        // key from the Toolbox `value_prop_bar_block`.
        value_props: (block: { value: Immutable<ValuePropBarBlockValue> }) => (
            <ValuePropsBarBlock valuePropsData={block.value} />
        ),

        // `pre_qualification_bar`: StaticBlock with no value; the legacy
        // `pre-qualification-homepage.html` gated the interactive
        // `FinancingModalTriggerHomepage` island on
        // `PreQualificationSettings.toolbox`. Render the real island (so it
        // hydrates) only when the side-channel gate is true.
        pre_qualification_bar: (block: { id?: string }) => {
            const enabled = block.id
                ? props.prequal_enabled_ssr[block.id]
                : false;
            return enabled ? (
                // SSR boundary div: required so useAppSelector in the child
                // reads fresh store state during SSR (mirrors
                // FinancingModalTriggerHomepageTemplate).
                <div>
                    <FinancingModalTriggerHomepage applicationSource="Toolbox" />
                </div>
            ) : null;
        },

        // `reviews_module` -> FourByOneImageBlock. `reviews_url` (legacy
        // `{% slugurl 'reviews' %}`) from the side-channel, keyed by block.id.
        reviews_module: (block: {
            id?: string;
            value: Immutable<FourByOneImageBlockValue>;
        }) => (
            <FourByOneImageBlock
                value={block.value}
                reviews_url={
                    (block.id ? props.reviews_url_ssr[block.id] : "") ?? ""
                }
            />
        ),

        // `multi_prod_module` -> HomeMultiProdModule.
        multi_prod_module: (block: {
            value: Immutable<HomeMultiProdModuleValue>;
        }) => <HomeMultiProdModule value={block.value} />,

        // `ugc_grid` -> UGCGrid (converted off the JSON-string contract).
        ugc_grid: (block: { value: Immutable<UGCGridValue> }) => (
            <UGCGridBlock value={block.value} />
        ),
    };
    return map;
};

export const HomePageBody = (props: IProps): React.JSX.Element | null => {
    // `modules` is nullable (`null=True, blank=True`).
    if (!props.page.modules) {
        return null;
    }
    const map = buildHomePageBodyComponentsMap(props);
    return (
        <>
            {props.page.modules.value.map((block) => (
                <StreamField
                    key={block.id}
                    tagName="div"
                    attrs={{
                        className: `w-block-${block.type} block-${block.type}`,
                    }}
                    value={[block]}
                    components={map}
                />
            ))}
        </>
    );
};
